package top.wcpe.allincore.ui.runtime.reactive

import top.wcpe.allincore.ui.runtime.internal.concurrent.newIdentitySet
import top.wcpe.allincore.ui.runtime.lifecycle.Pausable

/**
 * Effect 副作用体（SAM）：替代 `java.lang.Runnable`（commonMain 禁 java.*）。
 *
 * 刻意用 fun interface 而非 `() -> Unit`：SAM 形态让既有 Java 调用点（jvmTest 大量
 * `Reactive.effect(() -> ...)`）与 Kotlin lambda 调用点均零改动（Java lambda 无需 return Unit.INSTANCE）。
 */
fun interface EffectBody {
    /** 执行一次副作用。 */
    fun run()
}

/**
 * 副作用：构造时立即执行一次并追踪依赖；任一依赖变化时同步重跑；[dispose] 后不再跑。
 *
 * 追踪期间「读取即订阅」：runnable 内任何对已读 Signal 的同步写入会立即递归触发本 Effect，
 * 进而被深度计数捕获为响应式循环。
 */
class Effect(private val runnable: EffectBody) :
    Pausable,
    Subscriber {

    /** 当前已订阅的依赖集合（按身份比较）。 */
    private var deps: MutableSet<Trackable> = newIdentitySet()

    private var disposed: Boolean = false
    private var paused: Boolean = false

    /** 重组（重跑）计数：构造首跑与每次依赖变化重跑各 +1。 */
    private var runCount: Int = 0

    init {
        run()
        ReactiveMetricsSink.recordEffectCreated()
    }

    /** 当前重组计数；供 [ReactiveContext.currentEffectRunCount] 读取。 */
    fun runCount(): Int = runCount

    /** 执行一次副作用：先解绑旧依赖，再压栈追踪新依赖。 */
    private fun run() {
        if (disposed || paused) return
        runCount++
        ReactiveMetricsSink.recordRecompose()
        for (t in deps) t.unsubscribe(this)
        deps.clear()

        val collected: MutableSet<Trackable> = newIdentitySet()
        if (!ReactiveContext.enterEffect()) return
        ReactiveContext.pushTracker(ReactiveContext.Frame(this, collected))
        try {
            runnable.run()
        } finally {
            ReactiveContext.popTracker()
            ReactiveContext.exitEffect()
        }
        deps = collected
    }

    override fun onChange() {
        if (paused || disposed) return
        run()
    }

    override fun pause() {
        if (disposed || paused) return
        paused = true
        for (t in deps) t.unsubscribe(this)
    }

    override fun resume() {
        if (disposed || !paused) return
        paused = false
        run()
    }

    override fun isPaused(): Boolean = paused

    override fun dispose() {
        if (disposed) return
        disposed = true
        ReactiveMetricsSink.recordEffectDisposed()
        for (t in deps) t.unsubscribe(this)
        deps.clear()
    }
}
